import { cn } from "@/lib/utils";
import { useState } from "react";

const MOOSHIM_ITEMS = [
  {
    emoji: "🐴",
    title: "Mooshim #1 – Horse",
    alt: "Lil' Dragon Mooshim Horse",
    description:
      "Strong and steady like a horse, with a solid stance and calm breathing.",
  },
  {
    emoji: "🐻",
    title: "Mooshim #2 – Bear (Back)",
    alt: "Lil' Dragon Mooshim Bear (Back)",
    description:
      "Heavy and powerful like a bear, with strong back stance and big breathing.",
  },
  {
    emoji: "🐮",
    title: "Mooshim #3 – Bull (Front)",
    alt: "Lil' Dragon Mooshim Bull (Front)",
    description:
      "Charging forward like a bull, with strong front stance and brave breathing.",
  },
  {
    emoji: "🐱",
    title: "Mooshim #4 – Cat",
    alt: "Lil' Dragon Mooshim Cat",
    description: "Light and sneaky like a cat, landing softly with quiet breathing.",
  },
  {
    emoji: "🐯",
    title: "Mooshim #5 – Tiger",
    alt: "Lil' Dragon Mooshim Tiger",
    description:
      "Fast and fierce like a tiger, with sharp eyes and strong, snappy breathing.",
  },
] as const;

interface LilDragonsMooshimTabContentProps {
  hasTrainingVideo: (aliases: string[]) => boolean;
  handleWatchTrainingVideo: (aliases: string[]) => void;
}

export function LilDragonsMooshimTabContent({
  hasTrainingVideo,
  handleWatchTrainingVideo,
}: LilDragonsMooshimTabContentProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const active = MOOSHIM_ITEMS[activeIndex];

  return (
    <div>
      {/* Mobile only: title → video link → emoji nav → selected stance */}
      <div className="flex flex-col gap-4 md:hidden">
        <div className="flex flex-col gap-2">
          <h3 className="text-lg font-semibold text-taekwondo-red">Mooshim</h3>
          {hasTrainingVideo(["mooshim"]) && (
            <button
              type="button"
              className="inline-flex w-fit items-center text-sm font-medium text-taekwondo-blue hover:underline"
              onClick={() => handleWatchTrainingVideo(["mooshim"])}
            >
              Watch Demonstration Video
            </button>
          )}
        </div>
        <div
          className="flex w-full justify-between gap-1"
          role="tablist"
          aria-label="Mooshim animal stances"
        >
          {MOOSHIM_ITEMS.map((item, index) => {
            const isSelected = activeIndex === index;
            return (
              <button
                key={item.title}
                type="button"
                role="tab"
                aria-selected={isSelected}
                aria-label={item.title}
                onClick={() => setActiveIndex(index)}
                className={cn(
                  "flex min-w-0 flex-1 flex-col items-center justify-center rounded-xl border-2 bg-white px-1 py-2 transition-colors",
                  isSelected
                    ? "border-taekwondo-blue shadow-md shadow-taekwondo-blue/15 ring-2 ring-taekwondo-blue/25"
                    : "border-gray-200 hover:border-taekwondo-blue/40",
                )}
              >
                <span className="text-2xl leading-none sm:text-3xl" aria-hidden>
                  {item.emoji}
                </span>
              </button>
            );
          })}
        </div>
        <div
          className="bg-white rounded-lg p-4 shadow-sm flex flex-col gap-4"
          role="tabpanel"
        >
          <div className="flex w-full items-center justify-between gap-4">
            <img
              src="/images/dragons/dragon_yay.png"
              alt={active.alt}
              className="h-20 w-auto flex-shrink-0"
              loading="lazy"
              decoding="async"
            />
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-gray-100 text-5xl font-bold text-[#1F2933]">
              {active.emoji}
            </div>
          </div>
          <div className="text-left text-sm text-gray-700">
            <h4 className="text-md font-semibold mb-1">{active.title}</h4>
            <p>{active.description}</p>
          </div>
        </div>
      </div>

      {/* Desktop: direct copy of original page markup */}
      <div className="mt-10 hidden md:block">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-4">
          <h3 className="text-lg font-semibold text-taekwondo-red">Mooshim</h3>
          {hasTrainingVideo(["mooshim"]) && (
            <button
              type="button"
              className="inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
              onClick={() => handleWatchTrainingVideo(["mooshim"])}
            >
              Watch Demonstration Video
            </button>
          )}
        </div>
        <div className="space-y-4">
          {/* Mooshim #1 - Horse */}
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm flex flex-col md:flex-row items-center gap-4">
            <div className="flex w-full items-center justify-between md:hidden gap-4">
              <img
                src="/images/dragons/dragon_yay.png"
                alt="Lil' Dragon Mooshim Horse"
                className="h-20 w-auto flex-shrink-0"
              />
              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center text-5xl font-bold text-[#1F2933]">
                🐴
              </div>
            </div>
            <div className="hidden md:flex items-center gap-4 w-full">
              <img
                src="/images/dragons/dragon_yay.png"
                alt="Lil' Dragon Mooshim Horse"
                className="h-24 w-auto flex-shrink-0"
              />
              <div className="flex-1 text-left text-sm text-gray-700">
                <h4 className="text-md font-semibold mb-1">Mooshim #1 &ndash; Horse</h4>
                <p>
                  Strong and steady like a horse, with a solid stance and calm breathing.
                </p>
              </div>
              <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center text-5xl font-bold text-[#1F2933]">
                🐴
              </div>
            </div>
            <div className="md:hidden text-left text-sm text-gray-700 mt-2">
              <h4 className="text-md font-semibold mb-1">Mooshim #1 &ndash; Horse</h4>
              <p>
                Strong and steady like a horse, with a solid stance and calm breathing.
              </p>
            </div>
          </div>

          {/* Mooshim #2 - Bear (Back) */}
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm flex flex-col md:flex-row items-center gap-4">
            <div className="flex w-full items-center justify-between md:hidden gap-4">
              <img
                src="/images/dragons/dragon_yay.png"
                alt="Lil' Dragon Mooshim Bear (Back)"
                className="h-20 w-auto flex-shrink-0"
              />
              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center text-5xl font-bold text-[#1F2933]">
                🐻
              </div>
            </div>
            <div className="hidden md:flex items-center gap-4 w-full">
              <img
                src="/images/dragons/dragon_yay.png"
                alt="Lil' Dragon Mooshim Bear (Back)"
                className="h-24 w-auto flex-shrink-0"
              />
              <div className="flex-1 text-left text-sm text-gray-700">
                <h4 className="text-md font-semibold mb-1">Mooshim #2 &ndash; Bear (Back)</h4>
                <p>
                  Heavy and powerful like a bear, with strong back stance and big breathing.
                </p>
              </div>
              <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center text-5xl font-bold text-[#1F2933]">
                🐻
              </div>
            </div>
            <div className="md:hidden text-left text-sm text-gray-700 mt-2">
              <h4 className="text-md font-semibold mb-1">Mooshim #2 &ndash; Bear (Back)</h4>
              <p>
                Heavy and powerful like a bear, with strong back stance and big breathing.
              </p>
            </div>
          </div>

          {/* Mooshim #3 - Bull (Front) */}
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm flex flex-col md:flex-row items-center gap-4">
            <div className="flex w-full items-center justify-between md:hidden gap-4">
              <img
                src="/images/dragons/dragon_yay.png"
                alt="Lil' Dragon Mooshim Bull (Front)"
                className="h-20 w-auto flex-shrink-0"
              />
              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center text-5xl font-bold text-[#1F2933]">
                🐮
              </div>
            </div>
            <div className="hidden md:flex items-center gap-4 w-full">
              <img
                src="/images/dragons/dragon_yay.png"
                alt="Lil' Dragon Mooshim Bull (Front)"
                className="h-24 w-auto flex-shrink-0"
              />
              <div className="flex-1 text-left text-sm text-gray-700">
                <h4 className="text-md font-semibold mb-1">Mooshim #3 &ndash; Bull (Front)</h4>
                <p>
                  Charging forward like a bull, with strong front stance and brave breathing.
                </p>
              </div>
              <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center text-5xl font-bold text-[#1F2933]">
                🐮
              </div>
            </div>
            <div className="md:hidden text-left text-sm text-gray-700 mt-2">
              <h4 className="text-md font-semibold mb-1">Mooshim #3 &ndash; Bull (Front)</h4>
              <p>
                Charging forward like a bull, with strong front stance and brave breathing.
              </p>
            </div>
          </div>

          {/* Mooshim #4 - Cat */}
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm flex flex-col md:flex-row items-center gap-4">
            <div className="flex w-full items-center justify-between md:hidden gap-4">
              <img
                src="/images/dragons/dragon_yay.png"
                alt="Lil' Dragon Mooshim Cat"
                className="h-20 w-auto flex-shrink-0"
              />
              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center text-5xl font-bold text-[#1F2933]">
                🐱
              </div>
            </div>
            <div className="hidden md:flex items-center gap-4 w-full">
              <img
                src="/images/dragons/dragon_yay.png"
                alt="Lil' Dragon Mooshim Cat"
                className="h-24 w-auto flex-shrink-0"
              />
              <div className="flex-1 text-left text-sm text-gray-700">
                <h4 className="text-md font-semibold mb-1">Mooshim #4 &ndash; Cat</h4>
                <p>Light and sneaky like a cat, landing softly with quiet breathing.</p>
              </div>
              <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center text-5xl font-bold text-[#1F2933]">
                🐱
              </div>
            </div>
            <div className="md:hidden text-left text-sm text-gray-700 mt-2">
              <h4 className="text-md font-semibold mb-1">Mooshim #4 &ndash; Cat</h4>
              <p>Light and sneaky like a cat, landing softly with quiet breathing.</p>
            </div>
          </div>

          {/* Mooshim #5 - Tiger */}
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm flex flex-col md:flex-row items-center gap-4">
            <div className="flex w-full items-center justify-between md:hidden gap-4">
              <img
                src="/images/dragons/dragon_yay.png"
                alt="Lil' Dragon Mooshim Tiger"
                className="h-20 w-auto flex-shrink-0"
              />
              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center text-5xl font-bold text-[#1F2933]">
                🐯
              </div>
            </div>
            <div className="hidden md:flex items-center gap-4 w-full">
              <img
                src="/images/dragons/dragon_yay.png"
                alt="Lil' Dragon Mooshim Tiger"
                className="h-24 w-auto flex-shrink-0"
              />
              <div className="flex-1 text-left text-sm text-gray-700">
                <h4 className="text-md font-semibold mb-1">Mooshim #5 &ndash; Tiger</h4>
                <p>
                  Fast and fierce like a tiger, with sharp eyes and strong, snappy breathing.
                </p>
              </div>
              <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center text-5xl font-bold text-[#1F2933]">
                🐯
              </div>
            </div>
            <div className="md:hidden text-left text-sm text-gray-700 mt-2">
              <h4 className="text-md font-semibold mb-1">Mooshim #5 &ndash; Tiger</h4>
              <p>
                Fast and fierce like a tiger, with sharp eyes and strong, snappy breathing.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
